import React, { useState, useRef, useEffect } from 'react';
import { api, getImageUrl } from '../api';
import { takeDeepLink, useItemUrl } from '../utils/deepLink';
import { useCardImages } from '../utils/imageQueue';
import PhotoLightbox from './PhotoLightbox';
import BouquetCompositionDetail from './BouquetCompositionDetail';
import { useCatalogLabels, genitiveSingularLower } from '../catalogLabels';
import { componentName } from '../utils/componentName';

// compact — карточка в ленте «Есть сегодня» (как у ProductCard): без стрелок и точек на фото
function BouquetCard({ bouquet, addBouquetToCart, cartItem, updateBouquetQuantity, compact = false }) {
  const catalogLabels = useCatalogLabels();
  const quantityInCart = cartItem?.quantity || 0;
  // Дата заказа выбирается на чекауте → лимит «+» = effective_limit (max доступности по
  // достижимым датам с учётом корзины). today_available/next_better — информационные:
  //  - «В наличии сегодня N» (effective_today),
  //  - «с DATE M» (плановая волна, если она больше сегодняшней).
  const todayAvailable = bouquet.effective_today != null
    ? bouquet.effective_today
    : (bouquet.today_available != null ? bouquet.today_available : (bouquet.max_orderable || 0));
  // Лимит: сколько всего можно иметь в корзине (effective_limit считается без учёта своего
  // вклада → прибавляем то, что уже в корзине).
  const effectiveLimit = bouquet.effective_limit != null
    ? bouquet.effective_limit
    : Math.max(todayAvailable, bouquet.next_better_capacity || 0);
  const blockedByCart = !!bouquet.blocked_by_cart;
  // Cart-aware значения «с DATE: M» — приоритетнее сырого backend next_better_*.
  const nextBetterDate = bouquet.effective_future_date != null
    ? bouquet.effective_future_date
    : bouquet.next_better_date;
  const nextBetterCapacity = bouquet.effective_future_capacity != null
    ? bouquet.effective_future_capacity
    : (bouquet.next_better_capacity || 0);
  const maxOrderable = quantityInCart + effectiveLimit;
  // canAddMore: блокируем только если нет ни одной достижимой даты (effective_limit===0).
  // Если букет уже зарезервирован под стебли в корзине — блокируем +, нужно сначала
  // убрать стебли.
  const isReservedInCart = !!cartItem?.reserved_by;
  const hardBlocked = effectiveLimit === 0;
  const canAddMore = !isReservedInCart && !hardBlocked && quantityInCart + 1 <= maxOrderable;
  const [currentImage, setCurrentImage] = useState(0);

  const [showComposition, setShowComposition] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const hasRealImages = bouquet.images && bouquet.images.length > 0 && bouquet.images[0]?.image_url;
  const images = hasRealImages
    ? bouquet.images.map(img => ({
        small: img.image_small || img.thumb_url || img.image_url,
        medium: img.image_medium || img.image_url,
        large: img.image_large || img.image_url,
        default: img.image_medium || img.image_url,
      }))
    : [{ default: '💐' }];
  const totalImages = images.length || 1;
  const flipPhotos = !compact && totalImages > 1;
  // Фото в карточке — средний размер через очередь (utils/imageQueue.js), большое — в зуме
  const imageBoxRef = useRef(null);
  const cardUrls = hasRealImages ? images.map(img => getImageUrl(img.medium)) : [];
  const imageReady = useCardImages(cardUrls, imageBoxRef, currentImage);

  const finalPrice = bouquet.final_price || bouquet.price;
  const priceInCart = finalPrice * quantityInCart;

  const nextImage = () => setCurrentImage(p => (p + 1) % totalImages);
  const prevImage = () => setCurrentImage(p => (p - 1 + totalImages) % totalImages);

  // Свайп в сторону — листает фото на карточке; тап — полноэкранный просмотр.
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStartRef = useRef(null);

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const lastTouchEndRef = useRef(0);

  const handleTouchEnd = (e) => {
    lastTouchEndRef.current = Date.now();
    if (!touchStartRef.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;
    touchStartRef.current = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      if (!flipPhotos) return;
      if (dx < 0) nextImage(); else prevImage();
    } else if (Math.abs(dx) < 10 && Math.abs(dy) < 10 && hasRealImages) {
      // Тап-зоны: левые/правые 25% листают фото, центральные 50% открывают карточку
      const rect = e.currentTarget.getBoundingClientRect();
      const tapX = t.clientX - rect.left;
      if (flipPhotos && tapX < rect.width * 0.25) prevImage();
      else if (flipPhotos && tapX > rect.width * 0.75) nextImage();
      else openQuickView();
    }
  };

  const handleImageClick = (e) => {
    // Недавний тач уже обработан в handleTouchEnd — глушим синтезированный click,
    // но мышь/трекпад на тач-устройстве работает
    if (Date.now() - lastTouchEndRef.current < 700) return;
    if (!hasRealImages) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    if (flipPhotos && clickX < rect.width * 0.25) prevImage();
    else if (flipPhotos && clickX > rect.width * 0.75) nextImage();
    else openQuickView();
  };

  const handleAdd = (e) => {
    e.stopPropagation();
    if (canAddMore) addBouquetToCart(bouquet);
  };

  const handleInc = () => { if (canAddMore) updateBouquetQuantity(bouquet.id, 1); };
  const handleDec = () => updateBouquetQuantity(bouquet.id, -1);

  // Открытие quick-view: состав грузим ДО показа окна — иначе панель
  // «доезжает» после загрузки и фото сверху прыгает (сжимается)
  const openQuickView = () => {
    if (detail) { setLightboxOpen(true); return; }
    if (loadingDetail) return;
    setLoadingDetail(true);
    api.getBouquet(bouquet.id)
      .then(d => setDetail(d))
      .catch(() => {})
      .finally(() => {
        setLoadingDetail(false);
        setLightboxOpen(true); // открываем даже если состав не загрузился
      });
  };

  // Пришли по ссылке на этот букет (/bouquet/… или b_<id> из бота) — сразу открываем окно
  const cardRef = useRef(null);
  useEffect(() => {
    if (!takeDeepLink('bouquet', bouquet.id)) return;
    try { cardRef.current?.scrollIntoView({ block: 'center' }); } catch (e) {}
    if (hasRealImages) openQuickView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // На сайте открытое окно = адрес /bouquet/… в строке браузера, «Назад» его закрывает
  useItemUrl(lightboxOpen && hasRealImages, 'bouquet', bouquet, () => setLightboxOpen(false));

  const toggleComposition = (e) => {
    e.stopPropagation();
    const next = !showComposition;
    setShowComposition(next);
    if (next && !detail) {
      setLoadingDetail(true);
      api.getBouquet(bouquet.id)
        .then(d => setDetail(d))
        .catch(() => {})
        .finally(() => setLoadingDetail(false));
    }
  };

  const currentImg = images[currentImage] || images[0] || { default: '💐' };

  return (
    <div className="product-card" ref={cardRef} style={blockedByCart ? { opacity: 0.55 } : undefined}>
      <div className="product-image-container" ref={imageBoxRef} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onClick={handleImageClick}>
        <div className="product-image-placeholder">
          {hasRealImages ? (
            <>
              {images.map((img, index) => imageReady[index] && (
                <img
                  key={index}
                  src={cardUrls[index]}
                  alt={bouquet.name}
                  decoding="async"
                  onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                  style={{
                    position: index === 0 ? 'relative' : 'absolute',
                    top: 0, left: 0, width: '100%', height: '100%',
                    objectFit: 'cover',
                    opacity: index === currentImage ? 1 : 0,
                    transition: 'opacity 0.25s ease',
                    pointerEvents: 'none',
                  }}
                />
              ))}
            </>
          ) : (
            currentImg.default
          )}
        </div>

        {flipPhotos && (
          <>
            <span className="image-nav image-nav-prev"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); prevImage(); }}>‹</span>
            <span className="image-nav image-nav-next"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); nextImage(); }}>›</span>
            <div className="image-dots">
              {images.map((_, index) => (
                <span
                  key={index}
                  className={`image-dot ${index === currentImage ? 'active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setCurrentImage(index); }}
                  onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setCurrentImage(index); }}
                />
              ))}
            </div>
          </>
        )}

        <button
          className={`add-to-cart-btn ${!canAddMore ? 'disabled' : ''}`}
          onClick={handleAdd}
          onTouchEnd={(e) => e.stopPropagation()}
          aria-label="Добавить в корзину"
          disabled={!canAddMore}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <path d="M16 10a4 4 0 0 1-8 0"></path>
          </svg>
        </button>
      </div>

      <div className="product-info" style={{ position: 'relative' }}>
        <h3 className="product-name">{bouquet.name}</h3>

        {/* Composite-бейджи: сегодня (если есть) и будущая волна плановой (если есть). */}
        <div className="stock-labels">
          {todayAvailable > 0 && (
            <span className="stock-label stock-label--current">
              Готовы к сборке: {todayAvailable} шт
            </span>
          )}
          {nextBetterDate && nextBetterCapacity > 0 && (
            <span className="stock-label stock-label--future">
              С {new Date(nextBetterDate + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}: {nextBetterCapacity} шт
            </span>
          )}
          {/* Цветок ЗАНЯТ КОРЗИНОЙ (а не истёк по сроку), плановая откроет позже. */}
          {bouquet.occupied_by_cart && effectiveLimit > 0 && nextBetterDate && (
            <span className="stock-label" style={{ background: '#FFF3E0', color: '#8C5A1E' }}>
              Пересекается с другими позициями в корзине — доступно с {new Date(nextBetterDate + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
            </span>
          )}
          {blockedByCart && (
            <span className="stock-label" style={{ background: '#FFF3E0', color: '#8C5A1E' }}>
              Пересекается с другими позициями в корзине
            </span>
          )}
        </div>

        <div className="product-price-row">
          <div className="product-price-info">
            <span className="product-price">
              {(quantityInCart > 0 ? priceInCart : finalPrice).toLocaleString('ru-RU')} <span>₽</span>
            </span>
          </div>
        </div>

        {/* Кнопка раскрытия состава */}
        <button
          onClick={toggleComposition}
          style={{
            marginTop: 6,
            padding: '4px 8px 4px 0',
            border: 'none',
            background: 'transparent',
            color: '#1F3D36',
            fontSize: 12,
            cursor: 'pointer',
            textDecoration: 'underline',
            textDecorationStyle: 'dashed',
            textUnderlineOffset: 3,
            alignSelf: 'flex-start',
          }}
        >
          {showComposition ? 'Скрыть состав ▲' : 'Состав ▼'}
        </button>

        {showComposition && (
          <div style={{
            marginTop: 8,
            padding: '10px 0 0 0',
            borderTop: '1px solid #eee',
            fontSize: 12,
            color: '#333',
          }}>
            {loadingDetail && <div style={{ color: '#999' }}>Загружаем…</div>}
            {!loadingDetail && detail && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
                  {(detail.components || []).map(c => {
                    // line_total с бэка — уже учитывает пропорциональный скейл,
                    // если у букета установлена custom-цена. Fallback: qty × original.
                    const lineTotal = c.line_total != null
                      ? c.line_total
                      : (c.product_price || 0) * c.quantity;
                    return (
                      <div key={c.id} style={{ display: 'flex', gap: 4 }}>
                        {/* Количество — отдельным спаном: имя обрезается многоточием,
                            «×N» и цена видны всегда. ×N прижат к сумме (право),
                            имени достаётся максимум ширины */}
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {componentName(c)}
                        </span>
                        <span style={{ whiteSpace: 'nowrap', minWidth: 24, textAlign: 'right' }}>×{c.quantity}</span>
                        {/* Разделитель, чтобы «×2 280 ₽» не читалось как 2280 */}
                        <span style={{ color: '#bbb' }}>·</span>
                        <span style={{ color: '#666', whiteSpace: 'nowrap', textAlign: 'right', minWidth: 36 }}>
                          {lineTotal.toLocaleString('ru-RU')} ₽
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', gap: 8,
                  paddingTop: 6, borderTop: '1px solid #eee',
                  color: '#666',
                }}>
                  <span style={{ whiteSpace: 'nowrap' }}>Серв. сбор {detail.service_fee_percent ? `(${Math.round(detail.service_fee_percent)}%)` : ''}</span>
                  <span style={{ whiteSpace: 'nowrap' }}>{(detail.service_fee || 0).toLocaleString('ru-RU')} ₽</span>
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', gap: 8,
                  marginTop: 4, fontWeight: 600, color: '#1F3D36',
                }}>
                  <span style={{ whiteSpace: 'nowrap' }}>Итого</span>
                  <span style={{ whiteSpace: 'nowrap' }}>{(detail.final_price || 0).toLocaleString('ru-RU')} ₽</span>
                </div>
              </>
            )}
          </div>
        )}

        {quantityInCart > 0 && (
          <div className="quantity-controls" style={{ position: 'absolute', bottom: 0, right: 8, flexDirection: 'column', gap: 0 }}>
            <span className={`quantity-btn ${!canAddMore ? 'disabled' : ''}`} onClick={handleInc} style={{ opacity: canAddMore ? 1 : 0.4 }}>+</span>
            <span className="quantity">{quantityInCart}</span>
            <span className="quantity-btn" onClick={handleDec}>−</span>
          </div>
        )}
      </div>
      {lightboxOpen && hasRealImages && (
        <PhotoLightbox
          images={images.map(img => getImageUrl(img.large || img.default))}
          previews={cardUrls}
          startIndex={currentImage}
          alt={bouquet.name}
          onClose={() => setLightboxOpen(false)}
          onIndexChange={setCurrentImage}
          panel={
            <>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>{bouquet.name}</div>
              <BouquetCompositionDetail
                detail={detail}
                title={`Состав ${genitiveSingularLower(catalogLabels.bouquets) || ''}`.trim()}
              />
              {loadingDetail && !detail && (
                <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--text-secondary)' }}>Загружаем состав…</div>
              )}
              <div className="stock-labels" style={{ marginBottom: 10 }}>
                {todayAvailable > 0 && (
                  <span className="stock-label stock-label--current">Готовы к сборке: {todayAvailable} шт</span>
                )}
                {nextBetterDate && nextBetterCapacity > todayAvailable && (
                  <span className="stock-label stock-label--future">
                    С {new Date(nextBetterDate + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}: {nextBetterCapacity} шт
                  </span>
                )}
              </div>
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontSize: 20, fontWeight: 800 }}>
                  {(quantityInCart > 0 ? priceInCart : finalPrice).toLocaleString('ru-RU')}₽
                </span>
                {quantityInCart > 0 && (
                  <span className="product-step-badge" style={{ marginLeft: 8 }}>за {quantityInCart} шт</span>
                )}
              </div>
              {/* Одна строка действий постоянной высоты: после добавления панель
                  не растёт и фото сверху не сжимается */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {quantityInCart > 0 ? (
                  <>
                    <button className="primary-btn primary-btn--cart" style={{ padding: '8px 16px' }} onClick={handleDec}>−</button>
                    <span style={{ fontWeight: 700, minWidth: 24, textAlign: 'center' }}>{quantityInCart}</span>
                    <button className="primary-btn primary-btn--cart" style={{ padding: '8px 16px', opacity: canAddMore ? 1 : 0.4 }}
                      onClick={handleInc} disabled={!canAddMore}>+</button>
                    <button
                      className="primary-btn primary-btn--cart"
                      style={{ flex: 1, padding: '12px 16px' }}
                      onClick={() => {
                        setLightboxOpen(false);
                        window.dispatchEvent(new Event('app:go-to-cart'));
                      }}
                    >
                      Перейти в корзину
                    </button>
                  </>
                ) : (
                  <button className="primary-btn" onClick={handleAdd} disabled={!canAddMore}
                    style={{ width: '100%', padding: '12px 16px', opacity: canAddMore ? 1 : 0.5 }}>
                    В корзину
                  </button>
                )}
              </div>
            </>
          }
        />
      )}
    </div>
  );
}

export default BouquetCard;
